import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MoreHorizontal, Eye, Archive, Trash2, RotateCcw, Search, Loader2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import useConfirm from "@/hooks/useConfirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const ARCHIVE_ADMIN_ROLES = ["super_admin", "crm_admin", "admin"];
const ARCHIVABLE = ["draft", "pending_approval"];
const STATUS_LABEL = { draft: "Draft", pending_approval: "Awaiting approval" };
const label = (item, type) => `${type} ${item.number || item.quote_number || item.contract_number || ""} '${item.title || "Untitled"}'`;

/** Archive / restore / delete actions with confirmation. type: "quote" | "contract". */
export function useArchiveActions(token, onDone) {
  const confirm = useConfirm();
  const h = { Authorization: `Bearer ${token}` };
  const path = (type, id) => `${API}/${type === "quote" ? "quotes" : "contracts"}/${id}`;
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  return {
    archive: async (item, type) => {
      if (!(await confirm({ title: `Archive ${type}?`, body: `Archive ${label(item, type)}? It will move to the Archived tab and can be restored later.`, confirmLabel: "Archive", testid: "archive-confirm-dialog" }))) return;
      run(() => axios.post(`${path(type, item.id)}/archive`, {}, { headers: h }), `${type === "quote" ? "Quote" : "Contract"} archived`);
    },
    restore: async (item, type) => {
      if (!(await confirm({ title: `Restore ${type}?`, body: `Restore ${label(item, type)}? It goes back to its list as ${STATUS_LABEL[item.archived_prev_status] || "a draft"}.`, confirmLabel: "Restore", testid: "restore-confirm-dialog" }))) return;
      run(() => axios.post(`${path(type, item.id)}/restore`, {}, { headers: h }), `${type === "quote" ? "Quote" : "Contract"} restored`);
    },
    remove: async (item, type) => {
      if (!(await confirm({ title: `Delete ${type}?`, body: `Delete ${label(item, type)}? This can't be undone.`, confirmLabel: "Delete", confirmTone: "danger", testid: "delete-confirm-dialog" }))) return;
      run(() => axios.delete(path(type, item.id), { headers: h }), `${type === "quote" ? "Quote" : "Contract"} deleted`);
    },
  };
}

export function RowActionsMenu({ item, type, isAdmin, archived, onView, actions }) {
  const st = archived ? item.archived_prev_status : item.status;
  const canManage = isAdmin && ARCHIVABLE.includes(st);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`row-menu-${item.id}`} onClick={e => e.stopPropagation()} title="More actions" style={{ background: "none", border: "0.5px solid var(--border)", borderRadius: 8, padding: "5px 7px", cursor: "pointer", display: "inline-flex" }}>
          <MoreHorizontal style={{ width: 14, height: 14, color: "var(--text-tertiary)" }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
        <DropdownMenuItem data-testid={`row-menu-view-${item.id}`} onSelect={onView}><Eye style={{ width: 13, height: 13 }} /> View details</DropdownMenuItem>
        {isAdmin && archived && <DropdownMenuItem data-testid={`row-menu-restore-${item.id}`} onSelect={() => actions.restore(item, type)}><RotateCcw style={{ width: 13, height: 13 }} /> Restore</DropdownMenuItem>}
        {canManage && !archived && <DropdownMenuItem data-testid={`row-menu-archive-${item.id}`} onSelect={() => actions.archive(item, type)}><Archive style={{ width: 13, height: 13 }} /> Archive</DropdownMenuItem>}
        {canManage && <DropdownMenuItem data-testid={`row-menu-delete-${item.id}`} onSelect={() => actions.remove(item, type)} style={{ color: "var(--red)" }}><Trash2 style={{ width: 13, height: 13 }} /> Delete</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ArchivedRow({ item, actions, onView }) {
  const isQ = item.type === "quote";
  return (
    <div data-testid={`archived-item-${item.id}`} style={{ padding: "12px 20px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", gap: 14, borderLeft: "3px solid #d1d5db" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: isQ ? "var(--blue-light)" : "#F3E8FF", color: isQ ? "var(--blue)" : "#6B21A8" }}>{isQ ? "Quote" : "Contract"}</span>
          <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "monospace" }}>{item.number}</span>
          <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: "#F3F4F6", color: "#6B7280" }}>Archived</span>
          <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>was {STATUS_LABEL[item.archived_prev_status] || item.archived_prev_status}</span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-secondary, #555)" }}>{item.title || "Untitled"}</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
          {[item.contact_name, item.contact_company].filter(Boolean).join(" · ") || "No recipient"}
          {item.recipient_type === "ehi_client" && item.ehi_client_id ? ` · EHI Client ${item.ehi_client_id}` : ""}
          {" · "}Archived by {item.archived_by_name || "—"}{item.archived_at ? ` on ${new Date(item.archived_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-tertiary)", minWidth: 70, textAlign: "right" }}>${(item.total || 0).toLocaleString()}</div>
      <RowActionsMenu item={item} type={item.type} isAdmin archived onView={() => onView(item)} actions={actions} />
    </div>
  );
}

export function ArchivedPanel({ token, onChanged, onView }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const load = () => axios.get(`${API}/quotes-contracts/archived`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => setItems(r.data.items || [])).catch(() => { setItems([]); toast.error("Failed to load archived items"); });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per token
  useEffect(() => { load(); }, [token]);
  const actions = useArchiveActions(token, () => { load(); onChanged?.(); });
  const shown = useMemo(() => (items || []).filter(i => {
    if (kind !== "all" && i.type !== kind) return false;
    const s = q.trim().toLowerCase();
    return !s || [i.number, i.title, i.contact_name, i.contact_company, i.ehi_client_id].some(v => String(v || "").toLowerCase().includes(s));
  }), [items, q, kind]);

  return (
    <div data-testid="archived-panel">
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        {[["all", "All"], ["quote", "Quotes"], ["contract", "Contracts"]].map(([k, l]) => (
          <button key={k} data-testid={`archived-filter-${k}`} onClick={() => setKind(k)} style={{ padding: "5px 12px", fontSize: 12, borderRadius: 999, cursor: "pointer", fontFamily: "var(--font)", border: "0.5px solid var(--border)", background: kind === k ? "var(--blue)" : "var(--surface)", color: kind === k ? "#fff" : "var(--text-secondary)" }}>{l}</button>
        ))}
        <div style={{ position: "relative", marginLeft: "auto" }}>
          <Search style={{ position: "absolute", left: 9, top: 9, width: 13, height: 13, color: "var(--text-tertiary)" }} />
          <input data-testid="archived-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search number, title or recipient" style={{ fontSize: 12, padding: "7px 10px 7px 28px", border: "0.5px solid var(--border)", borderRadius: 8, width: 260, fontFamily: "var(--font)" }} />
        </div>
      </div>
      <div style={{ border: "0.5px solid var(--border)", borderRadius: 10, overflow: "hidden", background: "var(--surface)" }}>
        {items === null && <div data-testid="archived-loading" style={{ padding: 32, textAlign: "center" }}><Loader2 className="animate-spin" style={{ width: 18, height: 18, color: "var(--blue)" }} /></div>}
        {items !== null && shown.length === 0 && <div data-testid="archived-empty" style={{ padding: 40, textAlign: "center", fontSize: 13, color: "var(--text-tertiary)" }}>{items.length ? "No archived items match." : "Nothing archived yet."}</div>}
        {shown.map(i => <ArchivedRow key={`${i.type}-${i.id}`} item={i} actions={actions} onView={onView} />)}
      </div>
    </div>
  );
}
